import React, { useCallback, useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader } from '@/components/custom/loader';
import { Button } from '@/components/ui/button';
import { SelectNative } from '@/components/ui/select-native';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DashboardContent } from '@/layouts/dashboard';
import { PATHS } from '@/routes/paths';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import tiaTrilhaEscolha from '@/assets/images/tia/TIA-TRILHA-ESCOLHA.png';
import { TiaBanner } from '@/components/custom/tia-banner';
import { CreateTrilhaDialog } from './create-trilha-dialog';
import { FeaturedTrilhaCard, CreateTrilhaCard, TrilhaButton, TrilhaRow } from './trilha-list-items';
import { STATUS, STATUS_LABEL, buildTrilhaRows, pickFeatured, sameId } from './trilha-list-utils';

const STATUS_OPTIONS = [STATUS.NOT_STARTED, STATUS.IN_PROGRESS];

export function LearningPathGate() {
  const client = useClient();
  const navigate = useNavigate();
  const playful = usePlayfulStudent();
  const [paths, setPaths] = useState([]); // templates (catálogo)
  const [trails, setTrails] = useState([]); // trilhas do aluno (GET /trails)
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null); // id do template adaptive com o dialog aberto
  const [statusFilter, setStatusFilter] = useState('');
  const [deleting, setDeleting] = useState(null); // linha com a confirmação de exclusão aberta
  const [deleteBusy, setDeleteBusy] = useState(false);

  // `silent`: recarrega sem trocar a tela pelo loader (ex.: trilha recém-criada).
  const load = useCallback(
    ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError(false);
      }
      Promise.all([client.fetch('learning_paths'), client.fetch('trails')])
        .then(([templates, mine]) => {
          setPaths(templates.data || []);
          setTrails(mine.data || []);
          setLoading(false);
        })
        .catch(() => {
          if (silent) return;
          setError(true);
          setLoading(false);
        });
    },
    [client]
  );

  useEffect(() => {
    load();
  }, [load]);

  const adaptiveTemplate = paths.find(p => p.kind === 'adaptive');
  const rows = buildTrilhaRows(paths, trails);
  const featured = pickFeatured(rows);
  const listed = rows.filter(r => r !== featured && (!statusFilter || r.status === statusFilter));

  const openChapter = (trailId, chapterId) => navigate(PATHS.LEARNING_PATH.CHAPTER(trailId, chapterId));

  // Trilha do aluno abre no último capítulo; template fixo ainda não iniciado cria a trilha antes.
  const access = async row => {
    if (row.trailId) {
      openChapter(row.trailId, row.chapterId);
      return;
    }
    setStarting(true);
    const toastId = toast.loading('Preparando sua trilha...');
    const response = await client.mutate('user_learning_paths', { 'learning-path-id': row.learningPathId }, { method: 'POST' });
    setStarting(false);
    if (!response?.data) {
      toast.error(response?.errors?.[0]?.detail || 'Não foi possível acessar a trilha.', { id: toastId });
      return;
    }
    toast.dismiss(toastId);
    openChapter(response.data['trail-id'], response.data.id);
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    const response = await client.delete(['trails', deleting.trailId]);
    setDeleteBusy(false);
    // 422 traz a mensagem em `errors[0].detail`; o 404 vem sem corpo (cai na genérica).
    if (response?.error || response?.errors) {
      toast.error(response?.errors?.[0]?.detail || 'Não foi possível excluir a trilha.');
      return;
    }
    setTrails(prev => prev.filter(trail => !sameId(trail.id, deleting.trailId)));
    setDeleting(null);
    toast.success('Trilha excluída com sucesso.');
  };

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [{ route: PATHS.LEARNING_PATH.ROOT, name: 'Trilha', isActive: true }]
  };

  const hasHero = featured || adaptiveTemplate;
  const hasAnyRow = rows.some(r => r !== featured);

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      {loading && <Loader />}

      {!loading && error && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-neutral-100 bg-white p-10 text-center shadow-card">
          <h2 className="text-lg font-bold text-neutral-900">Não foi possível carregar as trilhas</h2>
          <p className="text-sm text-neutral-500">Tente novamente em instantes.</p>
          <Button
            type="button"
            variant="primary"
            onClick={() => load()}
          >
            Tentar de novo
          </Button>
        </div>
      )}

      {!loading && !error && (
        <div className="w-full">
          {playful ? (
            <TiaBanner
              className="mb-6 md:mt-16"
              image={tiaTrilhaEscolha}
              imageHeight={170}
              imageOffset={150}
              bubbleMaxWidth={530}
              text="Escolha uma trilha e avance bloco a bloco, no seu ritmo. Bora começar? 🗺️"
            />
          ) : (
            <header className="mb-7">
              <h1 className="text-2xl font-bold text-neutral-900">Escolha sua trilha</h1>
              <p className="mt-1 text-[15px] text-neutral-500">Um caminho montado pra você evoluir no seu ritmo, vencendo bloco a bloco.</p>
            </header>
          )}

          {!hasHero && !hasAnyRow && <p className="text-center text-sm text-neutral-500">Nenhuma trilha disponível no momento.</p>}

          {hasHero && (
            <div className={`grid grid-cols-1 gap-4 ${featured && adaptiveTemplate ? 'lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)]' : ''}`}>
              {featured && (
                <FeaturedTrilhaCard
                  row={featured}
                  starting={starting}
                  onContinue={() => access(featured)}
                  onDelete={setDeleting}
                />
              )}
              {adaptiveTemplate && <CreateTrilhaCard onCreate={() => setCreatingFor(adaptiveTemplate.id)} />}
            </div>
          )}

          {hasAnyRow && (
            <section className={hasHero ? 'mt-6 border-t border-neutral-100 pt-4' : ''}>
              <h2 className="text-xs font-bold uppercase tracking-wide text-neutral-500">Minhas trilhas</h2>

              <div className="mt-4 w-full max-w-[200px]">
                <Label
                  htmlFor="trilha-status-filter"
                  className="mb-1 block text-sm font-semibold text-neutral-600"
                >
                  Status da trilha
                </Label>
                <SelectNative
                  id="trilha-status-filter"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">Selecione um status</option>
                  {STATUS_OPTIONS.map(status => (
                    <option
                      key={status}
                      value={status}
                    >
                      {STATUS_LABEL[status]}
                    </option>
                  ))}
                </SelectNative>
              </div>

              <div className="mt-5 grid gap-3">
                {listed.length === 0 ? (
                  <p className="py-6 text-center text-sm text-neutral-500">Nenhuma trilha com esse status.</p>
                ) : (
                  listed.map((row, i) => (
                    <TrilhaRow
                      key={row.key}
                      row={row}
                      index={i}
                      starting={starting}
                      onAccess={access}
                      onDelete={setDeleting}
                    />
                  ))
                )}
              </div>
            </section>
          )}
        </div>
      )}

      {creatingFor != null && (
        <CreateTrilhaDialog
          learningPathId={creatingFor}
          onClose={() => setCreatingFor(null)}
          onCreated={() => load({ silent: true })}
        />
      )}

      <Dialog
        open={deleting != null}
        onOpenChange={open => !open && !deleteBusy && setDeleting(null)}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Apagar trilha?</DialogTitle>
            <DialogDescription>Tem certeza que deseja apagar essa trilha? Todo o seu progresso será perdido</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:space-x-0">
            <TrilhaButton
              variant="outline"
              disabled={deleteBusy}
              onClick={() => setDeleting(null)}
              className="sm:w-auto sm:px-6"
            >
              Cancelar
            </TrilhaButton>
            <TrilhaButton
              disabled={deleteBusy}
              onClick={confirmDelete}
              className="sm:w-auto sm:px-6"
            >
              Apagar
            </TrilhaButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
